// @flow
import React, { useContext } from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { Heading, MediaQuery } from '@riseart/common';
import { Section, Wrapper, wrapperPaddings } from '@riseart/layout';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { actionFactory } from 'shared_services/riseart/utils/Utils';
import { selectVisitorId } from 'shared_services/redux/selectors/visitor';
import { SSRContext } from 'shared_data/providers/ssr/Context';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { Breadcrumbs } from 'shared_components/common/breadcrumbs/Breadcrumbs';
import { SimilarArtworksLink } from 'shared_components/arts/widgets/SimilarArtworksLink';
import { HOCFilterContainer } from 'shared_hocs/filters/Container';
import { useApplicationMessages, DEVICE } from 'shared_hocs/messages/application/hooks';
import { WishlistFiltersArt } from 'shared_components/wishlist/filters/Art';
import {
  pagination as PAGINATION_ENUM,
  filter as FILTER_ENUM,
  analytics as ENUM_ANALYTICS,
} from 'Enum';
import { NoWishlistItems } from 'shared_components/wishlist/NoWishlistItems';
import HOCFilterPageRoute from 'shared_hocs/routes/FilterPageRoute';
import { HOCArtsGrid } from 'shared_hocs/arts/Grid';
import { ArtListSection } from 'shared_components/arts/containers/sections/List';
import { GOOGLE_TAGMANAGER_ACTIONS } from 'shared_data/providers/google/tagmanager/Actions';
import FILTER_VISITOR_FAVORITES_ART_QUERY from 'shared_data/queries/filter/visitorFavorites.graphql';

import {
  raScreenMd,
  raScreenMdMax,
  raScreenLg,
} from '@riseart/antd-provider/dist/website/variables.less';
import { similarArtworkslLinkCls } from 'shared_components/arts/widgets/SimilarArtworksLink.less';

const HOCArtsFiltersContainer = HOCFilterContainer(WishlistFiltersArt, {
  domain: FILTER_ENUM.domain.FAVORITES_ART,
});
const ArtsGrid = HOCArtsGrid(ArtListSection, { domain: FILTER_ENUM.domain.FAVORITES_ART });

/**
 * MeWishlist
 *
 * @param {*} props
 * @returns {Node}
 */
const MeWishlist = (props: Object): Node => {
  const { isSSR } = useContext(SSRContext);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const visitorId = useSelector(selectVisitorId);
  const pageTitle = formatMessage({ id: 'components.wishlist.title' });
  const { getFlatMessages } = useApplicationMessages({
    location: props.location,
    messageLocation: 'wishlist',
    device:
      (props.isMobile && DEVICE.MOBILE) ||
      (props.isTablet && DEVICE.TABLET) ||
      (props.isDesktop && DEVICE.DESKTOP) ||
      null,
  });

  return (
    <LayoutDefault>
      <Section padding="none" background="gray">
        <Wrapper>
          <Breadcrumbs
            links={[
              {
                isLink: false,
                name: pageTitle,
              },
            ]}
          />

          <Heading tag="h2" align="center">
            {pageTitle}
          </Heading>
        </Wrapper>
        <HOCArtsFiltersContainer
          domain={props.domain}
          additionalFilterQueryParams={{ visitorId }}
        />
      </Section>
      <ArtsGrid
        hideSort
        isSSR={isSSR}
        visitorId={visitorId}
        fetchQuery={FILTER_VISITOR_FAVORITES_ART_QUERY}
        fetchPolicy="no-cache"
        isSsrQuery
        promotionalCards={
          // Only show promotional cards if pagination type is infinite, which meants it is not a bot viewing the page
          props.paginationType === PAGINATION_ENUM.type.INFINITE
            ? getFlatMessages().filter(({ show }) => show)
            : null
        }
        showAddToCart
        domain={props.domain}
        wrapperPaddings={wrapperPaddings}
        pageUrl={props.pageUrl}
        noItems={<NoWishlistItems />}
        paginationType={props.paginationType}
        additionalActionControls={(artData: Object) =>
          artData.isProduct !== false ? (
            <SimilarArtworksLink
              url={UrlAssembler.artList({ search: { v: artData.id } })}
              className={similarArtworkslLinkCls}
              onClick={() => {
                dispatch(
                  actionFactory(GOOGLE_TAGMANAGER_ACTIONS.VISUAL_SEARCH_ADD, {
                    trigger: ENUM_ANALYTICS.visualSearch.trigger.WISHLIST,
                    artwork: artData,
                  }),
                );
              }}
            />
          ) : null
        }
        tracking={{
          name: ENUM_ANALYTICS.list.name.WISHLIST_ART_GRID,
          enabled: true,
          enableInitial: true,
          enableClick: true,
        }}
      />
    </LayoutDefault>
  );
};

const WishlistComponent = HOCFilterPageRoute(MeWishlist, FILTER_ENUM.domain.FAVORITES_ART);

export default function MeWishlistPage(props: any) {
  const match = useRouteMatch();
  const { translatedLocation: location } = useUrlParams();

  return (
    <MediaQuery minWidth={raScreenMd} maxWidth={raScreenMdMax}>
      {(isTablet) => (
        <MediaQuery minWidth={raScreenLg}>
          {(isDesktop) => (
            <WishlistComponent
              isMobile={!isTablet && !isDesktop}
              isTablet={isTablet}
              isDesktop={isDesktop}
              {...props}
              match={match}
              location={location}
              paginationType={PAGINATION_ENUM.type.INFINITE}
              domain={FILTER_ENUM.domain[props.routeConfig.filterDomainKey]}
            />
          )}
        </MediaQuery>
      )}
    </MediaQuery>
  );
}
